import React, { useState, useRef, useEffect, useContext, useCallback } from "react";
import { Outlet } from "react-router-dom";
import Header from "../../components/header/Header";
import Sidebar from "../../components/sidebar/Sidebar";
import { useNavigation } from "react-router-dom";
import TopBarProgress from "react-topbar-progress-indicator";
import { ActionSidebarsContext } from "../../context/actionSidebarsContext";
import { IActionSidebarsContext } from "../../types/actionSidebars";
import SidebarAddService from "../../components/sidebarAddService/SidebarAddService";
import SidebarModifyFleet from "../../components/sidebarModifyFleet/SidebarModifyFleet";
import SidebarAddRoute from "../../components/sidebarAddRoute/SidebarAddRoute";
import { useLocation } from "react-router-dom";
import { IEmissionClass, IEngineType, IVehicleInfo, Vehicle } from "../../types/vehicles";
import { ICountry as ICountryType } from "../../types/countries";
import { IServicesInfo as IServicesInfoType } from "../../types/servicesInfo";
import { FleetContext } from "../../context/fleetContext";
import { IFleetContext } from "../../types/fleet";
import { CountriesSelectOption } from "../../types/countries";
import { computeCountries } from "../../utils/helpers/computeCountries";
import { groupServicesInfo } from "../../utils/helpers/groupServicesInfo";
import type { ServicesInfoComputedType } from "../../types/servicesInfo";
import { Services as ServicesType } from "../../types/services";
import { IServicesContext, ServicesContext } from "../../context/servicesContext";
import { RoutesType } from "../../types/routes";
import { IRoutesContext, RoutesContext } from "../../context/routesContext";
import SidebarNotifications from "../../components/sidebarNotifications/SidebarNotifications";
import { UserRole } from "../../types/settings";
import { RolesContext } from "../../context/rolesContext";
import { IRolesContext } from "../../types/roles";
import { INotification, INotificationsContext } from "../../types/notifications";
import { NotificationsContext } from "../../context/notificationsContext";
import { BusinessProfile } from "../../types/profile";
import SidebarAddDriver from "../../components/sidebarAddDriver/SidebarAddDriver";
import SidebarAddAssociations from "../../components/sidebarAddAssociations/SidebarAddAssociations";
import { useNotificationsLoader } from "../../utils/hooks/useNotificationsLoader";
import UserNavigationTracker from "../../components/UserNavigationTracker/UserNavigationTracker";
import NavigationInsightDebug from "../../components/UserNavigationTracker/NavigationInsightDebug";
import { NavigationProvider } from "../../context/navigationContext";
import { useAsyncDataLoader } from "../../utils/hooks/useAsyncDataLoader";
import { useSidebarDataLoader } from "../../utils/hooks/useSidebarDataLoader";
import { useBusinessProfile } from "../../utils/hooks/useBusinessProfile";
import TokenRefreshTester from "../../components/TokenRefreshTester/TokenRefreshTester";

const ProtectedLayout: React.FC = () => {
	const navigation = useNavigation();
	const sidebarRef = useRef<HTMLElement>(null);
	const burgerRef = useRef<HTMLDivElement>(null);
	const location = useLocation();

	// Usa il nuovo hook per caricare i dati in parallelo
	const { isLoading, error } = useAsyncDataLoader();
	const { businessProfile } = useBusinessProfile();
	
	// Usa il hook per caricare i dati dei sidebars
	const { countries, servicesInfo, isLoading: sidebarDataLoading, error: sidebarDataError } = useSidebarDataLoader();

	// Debug logging per servicesInfo
	useEffect(() => {
		console.log('ProtectedLayout - servicesInfo:', servicesInfo);
		console.log('ProtectedLayout - servicesInfo mainServices:', servicesInfo?.mainServices?.length);
		console.log('ProtectedLayout - servicesInfo additionalServices:', servicesInfo?.additionalServices?.length);
	}, [servicesInfo]);

	// Load notifications asynchronously
	const { notifications: notificationsToReadApi } = useNotificationsLoader();

	// Context data
	const {
		setVehicles,
		setEmissionClass,
		setEngineType,
		setVehiclesInfo,
		vehiclesInfo,
		setIsIntermodal,
		setLastUpdate
	} = useContext(FleetContext) as IFleetContext;

	// Debug logging per vehiclesInfo
	useEffect(() => {
		console.log('ProtectedLayout - vehiclesInfo:', vehiclesInfo);
		console.log('ProtectedLayout - vehiclesInfo length:', vehiclesInfo?.length);
	}, [vehiclesInfo]);

	const { setServices } = useContext(ServicesContext) as IServicesContext;

	const { setRoutes } = useContext(RoutesContext) as IRoutesContext;

	const { setRoles } = useContext(RolesContext) as IRolesContext;

	const { setNotificationsToRead } = useContext(NotificationsContext) as INotificationsContext;

	// Set notifications to read when they are loaded asynchronously
	useEffect(() => {
		if (!!notificationsToReadApi) {
			setNotificationsToRead(notificationsToReadApi);
		}
	}, [notificationsToReadApi, setNotificationsToRead]);

	const [isOpenSidebarOnMobile, setIsOpenSidebarOnMobile] = useState(false);
	const {
		sidebarAddServiceOpen,
		setSidebarAddServiceOpen,
		sidebarModifyFleetOpen,
		setSidebarModifyFleetOpen,
		sidebarAddRouteOpen,
		setSidebarAddRouteOpen,
		sidebarNotificationsOpen,
		setSidebarNotificationsOpen,
		sidebarAddDriverOpen,
		setSidebarAddDriverOpen,
		sidebarAddVehicleOpen,
		setSidebarAddVehicleOpen,
		sidebarAddAssociationsOpen,
		setSidebarAddAssociationsOpen
	} = useContext(ActionSidebarsContext) as IActionSidebarsContext;

	// Menu Sidebar
	useEffect(() => {
		const listener = (e: any) => {
			if (sidebarRef && sidebarRef.current != null) {
				if (!sidebarRef.current.contains(e.target) && !burgerRef.current?.contains(e.target)) {
					setIsOpenSidebarOnMobile(false);
				}
			}
		}

		document.addEventListener('click', listener);

		return () => {
			document.removeEventListener('click', listener);
		}

	}, []);

	useEffect(() => {
		setIsOpenSidebarOnMobile(false);
		setSidebarAddServiceOpen(false);
		setSidebarModifyFleetOpen(false);
		setSidebarAddRouteOpen(false);
		setSidebarNotificationsOpen(false);
		setSidebarAddDriverOpen(false)

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.pathname]);

	TopBarProgress.config({
		barColors: {
			"0": "#fff",
			"1.0": "#fff"
		},
		shadowBlur: 5
	});

	const handleSidebarMobile = () => {
		setIsOpenSidebarOnMobile(!isOpenSidebarOnMobile);
	}

	const closeSidebarAddRoute = useCallback(() => {
		setSidebarAddRouteOpen(false)
	}, [])

	const closeSidebarModifyFleet = useCallback(() => {
		setSidebarModifyFleetOpen(false)
	}, []);

	const closeSidebarAddService = useCallback(() => {
		setSidebarAddServiceOpen(false)
	}, []);

	const closeSidebarNotifications = useCallback(() => {
		setSidebarNotificationsOpen(false);
	}, [])

	const closeSidebarAddDriver = useCallback(() => {
		setSidebarAddDriverOpen(false);
	}, [])

	const closeSidebarAddVehicle = useCallback(() => {
		setSidebarAddVehicleOpen(false);
	}, [])

	const closeSidebarAddAssociations = useCallback(()=>{
		setSidebarAddAssociationsOpen(false);
	},[])

	// Mostra errore se c'è un problema nel caricamento
	if (error || sidebarDataError) {
		return (
			<div className="h-[100dvh] flex flex-col overflow-hidden text-black">
				<Header
					callbackOpenCloseSidebarMobile={handleSidebarMobile}
					ref={burgerRef}
				/>
				<div className="grow flex flex-col relative">
					<main className="grow max-h-[calc(100dvh-60px)] overflow-auto lg:pl-20 md:bg-grey">
						<div className="md:p-8">
							<div className="bg-white min-h-[calc(100dvh-124px)] flex items-center justify-center">
								<div className="text-center">
									<p className="text-red-600 mb-4">Errore nel caricamento dei dati</p>
									<button 
										onClick={() => window.location.reload()} 
										className="px-4 py-2 bg-red text-white rounded hover:bg-red-700"
									>
										Riprova
									</button>
								</div>
							</div>
						</div>
					</main>
				</div>
			</div>
		);
	}

	return (
		<NavigationProvider>
			<div className="h-[100dvh] flex flex-col overflow-hidden text-black">
				<Header
					callbackOpenCloseSidebarMobile={handleSidebarMobile}
					ref={burgerRef}
				/>

				{
					navigation.state === "loading" &&
					<TopBarProgress />
				}

				<div className="grow flex flex-col relative">

					<Sidebar
						isOpenOnMobile={isOpenSidebarOnMobile}
						ref={sidebarRef}
						businessProfile={businessProfile}
					/>

					<main className="grow max-h-[calc(100dvh-60px)] overflow-auto lg:pl-20 md:bg-grey">
						<div className="md:p-8">
							<div className="bg-white min-h-[calc(100dvh-124px)]">
								<Outlet />
							</div>
						</div>
					</main>

				{/* ACTION SIDEBARS */}
				<SidebarAddService
					isOpen={sidebarAddServiceOpen}
					callbackCloseSidebar={closeSidebarAddService}
					servicesInfoObj={servicesInfo}
					setServiceInfo={() => {}} // Funzione vuota perché i dati vengono gestiti dal hook
				/>
				<SidebarModifyFleet
					isOpen={sidebarModifyFleetOpen}
					callbackCloseSidebar={closeSidebarModifyFleet}
					vehiclesInfo={vehiclesInfo}
				/>
				<SidebarAddRoute
					isOpen={sidebarAddRouteOpen}
					callbackCloseSidebar={closeSidebarAddRoute}
					options={countries}
				/>
				<SidebarNotifications
					isOpen={sidebarNotificationsOpen}
					callbackCloseSidebar={closeSidebarNotifications}
				/>
				<SidebarAddDriver
					isOpen={sidebarAddDriverOpen}
					callbackCloseSidebar={closeSidebarAddDriver}
				
				/>
			
				<SidebarAddAssociations
					isOpen={sidebarAddAssociationsOpen}
					callbackCloseSidebar={closeSidebarAddAssociations}
				/>
				
				{/* User Navigation Tracker - visibile in tutto l'applicativo */}
				{/* <UserNavigationTracker /> */}
				{/* <NavigationInsightDebug isVisible={true} /> */}
				{/* Token Refresh Tester - solo in development */}
				{/* <TokenRefreshTester /> */}
			</div>
		</div>
		</NavigationProvider>
	);
};

export default ProtectedLayout;